import type { FontsSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, round } from '../util/safe.js';
import { hashValue } from '../util/hash.js';

/**
 * Набор установленных шрифтов.
 *
 * Мощный маркер ОС: шрифты приходят вместе с системой и офисным софтом, а
 * подделать список целиком сложно — надо не только скрыть чужие, но и
 * согласованно показать свои. Меряем двумя независимыми способами и сверяем
 * их между собой: расхождение означает, что пропатчен ровно один из них.
 */

const BASE_FONTS = ['monospace', 'sans-serif', 'serif'];
const PROBE_TEXT = 'mmmmmmmmmmlli WwMmIi 0O';
const PROBE_SIZE = '72px';

const TEST_FONTS = [
  // Windows
  'Arial', 'Arial Black', 'Arial Narrow', 'Bahnschrift', 'Calibri', 'Cambria',
  'Candara', 'Comic Sans MS', 'Consolas', 'Constantia', 'Corbel',
  'Courier New', 'Ebrima', 'Franklin Gothic Medium', 'Gabriola', 'Gadugi',
  'Georgia', 'HoloLens MDL2 Assets', 'Impact', 'Ink Free', 'Javanese Text',
  'Leelawadee UI', 'Lucida Console', 'Lucida Sans Unicode', 'Malgun Gothic',
  'Marlett', 'Microsoft Himalaya', 'Microsoft JhengHei', 'Microsoft New Tai Lue',
  'Microsoft PhagsPa', 'Microsoft Sans Serif', 'Microsoft Tai Le',
  'Microsoft YaHei', 'Microsoft Yi Baiti', 'MingLiU-ExtB', 'Mongolian Baiti',
  'MS Gothic', 'MV Boli', 'Myanmar Text', 'Nirmala UI', 'Palatino Linotype',
  'Segoe MDL2 Assets', 'Segoe Print', 'Segoe Script', 'Segoe UI',
  'Segoe UI Emoji', 'Segoe UI Historic', 'Segoe UI Symbol', 'SimSun',
  'Sitka', 'Sylfaen', 'Symbol', 'Tahoma', 'Times New Roman', 'Trebuchet MS',
  'Verdana', 'Webdings', 'Wingdings', 'Yu Gothic',
  // macOS
  'American Typewriter', 'Andale Mono', 'Apple Chancery', 'Apple Color Emoji',
  'Apple SD Gothic Neo', 'AppleGothic', 'Avenir', 'Avenir Next', 'Baskerville',
  'Big Caslon', 'Bodoni 72', 'Bradley Hand', 'Brush Script MT', 'Chalkboard',
  'Chalkduster', 'Charter', 'Cochin', 'Copperplate', 'Didot', 'Futura',
  'Geneva', 'Gill Sans', 'Helvetica', 'Helvetica Neue', 'Herculanum',
  'Hoefler Text', 'Lucida Grande', 'Luminari', 'Marker Felt', 'Menlo',
  'Monaco', 'Noteworthy', 'Optima', 'Palatino', 'Papyrus', 'Phosphate',
  'Rockwell', 'Savoye LET', 'SignPainter', 'Skia', 'Snell Roundhand',
  'Times', 'Trattatello', 'Zapfino', 'SF Pro Text', 'SF Mono',
  // Linux
  'Bitstream Vera Sans', 'Cantarell', 'DejaVu Sans', 'DejaVu Sans Mono',
  'DejaVu Serif', 'FreeMono', 'FreeSans', 'FreeSerif', 'Liberation Mono',
  'Liberation Sans', 'Liberation Serif', 'Nimbus Mono PS', 'Nimbus Roman',
  'Nimbus Sans', 'Noto Color Emoji', 'Noto Sans', 'Noto Serif', 'Ubuntu',
  'Ubuntu Condensed', 'Ubuntu Mono', 'URW Bookman',
  // Android / кроссплатформенные
  'Droid Sans', 'Droid Sans Mono', 'Droid Serif', 'Roboto', 'Roboto Condensed',
  'Roboto Mono', 'Open Sans', 'PT Sans', 'Source Code Pro',
];

/** Шрифты-маркеры: наличие практически однозначно указывает на ОС. */
const OS_MARKERS: Record<string, string[]> = {
  windows: ['Segoe UI', 'Calibri', 'Segoe MDL2 Assets', 'Marlett', 'MS Gothic', 'Sylfaen'],
  macos: ['Helvetica Neue', 'Lucida Grande', 'Menlo', 'Zapfino', 'Apple Color Emoji', 'Skia'],
  linux: ['DejaVu Sans', 'Liberation Sans', 'Ubuntu', 'Cantarell', 'Nimbus Sans', 'FreeSans'],
  android: ['Droid Sans', 'Roboto Condensed', 'Noto Color Emoji'],
};

const EMOJI_PROBES = [
  '😃', '🧑‍🚀', '👨‍👩‍👧‍👦', '🏳️‍🌈', '👋🏽', '🇷🇺', '🫠', '🩷', '🪿', '🇺🇳', '⌚️', '🕰️',
];

interface Measurer {
  measure: (fontFamily: string) => { width: number; height: number };
  dispose: () => void;
}

function createMeasurer(): Measurer | null {
  return attempt(() => {
    const holder = document.createElement('div');
    holder.style.cssText =
      'position:absolute;left:-9999px;top:-9999px;visibility:hidden;' +
      'width:auto;height:auto;white-space:nowrap;line-height:normal;';
    const span = document.createElement('span');
    span.style.cssText = `font-size:${PROBE_SIZE};font-style:normal;font-weight:normal;letter-spacing:normal;`;
    span.textContent = PROBE_TEXT;
    holder.appendChild(span);
    document.body.appendChild(holder);

    return {
      measure(fontFamily: string) {
        span.style.fontFamily = fontFamily;
        return { width: span.offsetWidth, height: span.offsetHeight };
      },
      dispose() {
        attempt(() => holder.remove());
      },
    };
  });
}

function detectByMeasurement(): string[] | null {
  const measurer = createMeasurer();
  if (!measurer) return null;

  try {
    // Эталон: как выглядит текст, отрисованный каждым базовым семейством.
    const baseline: Record<string, { width: number; height: number }> = {};
    for (const base of BASE_FONTS) {
      baseline[base] = measurer.measure(base);
    }

    const detected: string[] = [];
    for (const font of TEST_FONTS) {
      // Шрифт считается установленным, если хотя бы с одним базовым
      // фолбэком метрики отличаются от эталона.
      const present = BASE_FONTS.some((base) => {
        const m = measurer.measure(`"${font}",${base}`);
        const b = baseline[base];
        return m.width !== b.width || m.height !== b.height;
      });
      if (present) detected.push(font);
    }
    return detected;
  } finally {
    measurer.dispose();
  }
}

/**
 * Второй, независимый способ: измерение через canvas.
 *
 * Специально не FontFaceSet.check — тот для локальных системных шрифтов
 * возвращает true независимо от того, установлены они или нет, и как детектор
 * наличия бесполезен. А вот canvas.measureText идёт мимо движка вёрстки:
 * получаем действительно другой путь до тех же данных, и его расхождение
 * с DOM-измерением уже о чём-то говорит.
 */
function detectByCanvas(): string[] | null {
  return attempt(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const baseline: Record<string, number> = {};
    for (const base of BASE_FONTS) {
      ctx.font = `${PROBE_SIZE} ${base}`;
      baseline[base] = ctx.measureText(PROBE_TEXT).width;
    }

    return TEST_FONTS.filter((font) =>
      BASE_FONTS.some((base) => {
        ctx.font = `${PROBE_SIZE} "${font}",${base}`;
        // Ширина строки меняется только если шрифт реально подставился.
        return Math.abs(ctx.measureText(PROBE_TEXT).width - baseline[base]) > 0.01;
      }),
    );
  });
}

/**
 * Определяет систему по отрисовке эмодзи.
 *
 * Меряется не ширина, а сами нарисованные пиксели. Ширину пробовали первой,
 * и живой замер показал, почему она не годится: `measureText` возвращает для
 * эмодзи один и тот же шаг независимо от глифа — и одиночный смайл, и семья
 * из четырёх человек, и флаг дали ровно по 32 пикселя. Признак, который
 * всегда возвращает одно число, не различает ничего.
 *
 * Пиксели различают. Опорных наблюдений два:
 *
 *  - Windows до сих пор не рисует флаги стран. Segoe UI Emoji показывает
 *    «RU» обычными буквами — одноцветными. Настоящий флаг многоцветен.
 *    Число различимых цветов и оказывается разделителем.
 *  - Составные эмодзи с соединителем ZWJ на Apple и Android склеиваются
 *    в один глиф, а без поддержки распадаются на отдельные символы и
 *    занимают заметно больше места. Здесь меряется ширина не по метрике
 *    шрифта, а по границам закрашенных пикселей.
 *
 * Различить Apple и Android этим способом нельзя, и попытки не делается:
 * для этого есть шрифты и голоса. Зато ловится главное — мобильный
 * User-Agent на настольной системе.
 */
interface Ink {
  /** Ширина закрашенной области в пикселях. */
  width: number;
  /** Число различимых цветов. Одноцветный глиф — это текст, а не эмодзи. */
  colors: number;
}

function measureInk(ctx: CanvasRenderingContext2D, text: string): Ink | null {
  return attempt(() => {
    const { width: w, height: h } = ctx.canvas;
    ctx.clearRect(0, 0, w, h);
    ctx.fillText(text, 4, h - 12);

    const data = ctx.getImageData(0, 0, w, h).data;
    let min = w;
    let max = -1;
    const palette = new Set<number>();

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        // Полупрозрачные краевые пиксели — сглаживание, а не глиф.
        if (data[i + 3] < 128) continue;
        if (x < min) min = x;
        if (x > max) max = x;
        // Цвет огрубляется до четырёх бит на канал: иначе сглаживание
        // одного и того же цвета насчитало бы сотни оттенков.
        palette.add(((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4));
      }
    }

    if (max < 0) return null;
    return { width: max - min + 1, colors: palette.size };
  });
}

function guessOsFromEmoji(): { guess: string | null; evidence: Record<string, number> | null } {
  const result = attempt(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 80;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.font = '48px sans-serif';
    ctx.textBaseline = 'alphabetic';

    const flag = measureInk(ctx, '\u{1F1F7}\u{1F1FA}');
    const person = measureInk(ctx, '\u{1F468}');
    const family = measureInk(ctx, '\u{1F468}\u200D\u{1F469}\u200D\u{1F467}\u200D\u{1F466}');
    if (!flag || !person || !family) return null;

    return {
      flagWidth: flag.width,
      flagColors: flag.colors,
      personWidth: person.width,
      personColors: person.colors,
      familyWidth: family.width,
    };
  });

  if (!result) return { guess: null, evidence: null };

  // Настоящий флаг многоцветен; буквы «RU» на месте флага одноцветны.
  // Порог с запасом: сглаживание добавляет промежуточные оттенки даже
  // одноцветному тексту.
  const flagRendered = result.flagColors >= 6;
  // Склеенная семья занимает примерно столько же, сколько один человек;
  // распавшаяся — вчетверо больше.
  const zwjJoined = result.familyWidth <= result.personWidth * 2;

  let guess: string | null = null;
  if (!flagRendered && zwjJoined) {
    guess = 'windows';
  } else if (flagRendered && zwjJoined) {
    guess = 'macos-or-ios-or-android';
  }
  // Прочие сочетания оставляем без вывода: гадать дороже, чем молчать.

  return { guess, evidence: result };
}

function measureEmoji(): string | null {
  return attempt(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.font = '32px sans-serif';
    // Ширина составных эмодзи выдаёт, склеил ли движок ZWJ-последовательность
    // в один глиф — это зависит от версии эмодзи-шрифта, то есть от ОС.
    const widths = EMOJI_PROBES.map((e) => round(ctx.measureText(e).width, 3));
    return hashValue(widths);
  });
}

function measureTextMetrics(): string | null {
  return attempt(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const samples: Record<string, unknown> = {};
    for (const font of ['16px serif', '16px sans-serif', '16px monospace', '20px "Segoe UI", "Helvetica Neue", sans-serif']) {
      ctx.font = font;
      const m = ctx.measureText('Ag ЖЩ 123 fi');
      samples[font] = {
        width: round(m.width, 4),
        actualLeft: round(m.actualBoundingBoxLeft, 4),
        actualRight: round(m.actualBoundingBoxRight, 4),
        actualAscent: round(m.actualBoundingBoxAscent, 4),
        actualDescent: round(m.actualBoundingBoxDescent, 4),
        fontAscent: round(m.fontBoundingBoxAscent, 4),
        fontDescent: round(m.fontBoundingBoxDescent, 4),
        emAscent: round(m.emHeightAscent ?? Number.NaN, 4),
        emDescent: round(m.emHeightDescent ?? Number.NaN, 4),
        alphabetic: round(m.alphabeticBaseline ?? Number.NaN, 4),
      };
    }
    return hashValue(samples);
  });
}

function guessOs(detected: string[]): string | null {
  if (detected.length === 0) return null;
  const found = new Set(detected);
  let best: string | null = null;
  let bestScore = 0;
  for (const [os, markers] of Object.entries(OS_MARKERS)) {
    const score = markers.filter((m) => found.has(m)).length / markers.length;
    if (score > bestScore) {
      bestScore = score;
      best = os;
    }
  }
  // Меньше трети маркеров — вывод недостаточно надёжен, чтобы на него ссылаться.
  return bestScore >= 0.34 ? best : null;
}

export const fontsModule = defineModule({
  id: 'fonts',
  phase: 1,
  timeoutMs: 6000,
  run(): FontsSignals {
    const detected = detectByMeasurement();
    const canvasDetected = detectByCanvas();
    const emoji = guessOsFromEmoji();

    let methodsAgree: boolean | null = null;
    if (detected && canvasDetected) {
      const a = new Set(detected);
      const b = new Set(canvasDetected);
      const union = new Set([...a, ...b]);
      let same = 0;
      for (const font of union) {
        if (a.has(font) === b.has(font)) same++;
      }
      // Небольшое расхождение методов нормально (разные пороги округления),
      // порогом отсекаем только грубые случаи.
      methodsAgree = union.size === 0 ? true : same / union.size >= 0.9;
    }

    return {
      detected,
      detectedCount: detected?.length ?? null,
      canvasDetected,
      methodsAgree,
      hash: detected ? hashValue(detected) : null,
      emojiHash: measureEmoji(),
      textMetricsHash: measureTextMetrics(),
      osGuess: detected ? guessOs(detected) : null,
      emojiOsGuess: emoji.guess,
      emojiEvidence: emoji.evidence,
    };
  },
});
